import { inject } from '@angular/core';
import { CanMatchFn, Route, Router } from '@angular/router';
import { FeatureFlagsService } from '../services/feature-flags.service';
import { filter, map, Observable, tap, withLatestFrom } from 'rxjs';

/**
 * Disallow to use route if Feature Flag is disabled.
 * 
 * Notice: Use only in a routes where user is logged in, because flags are loaded after user is logged in and this function
 * is waiting for features to be loaded.
 * 
 * Example Usage with children:
 * 
 * ```
 const routes: Route[] = [
  {
    path: "login",
    loadChildren: () =>
      import("./login/login.module").then(m => m.LoginModule),
    canLoad: [canLoadFeature],
    data: {
      feature: "<featureFlagKeyName>"
    }
  },
 * ```
 * 
 * Example Usage with component:
 * 
 * ```
 const routes: Route[] = [
  {
    path: "login",
    component: SomeComponent,
    canActivate: [canLoadFeature],
    data: {
      feature: "<featureFlagKeyName>"
    }
  },
 * ```
 * @param route 
 * @param segments 
 * @returns 
 */
export const canLoadFeature: CanMatchFn = (route: Route): Observable<boolean> => {
  const service = inject(FeatureFlagsService);
  const router = inject(Router);

  const flagKey = route?.data?.['feature'];

  return service.isFeatureEnabled$(flagKey).pipe(
    tap(isFlagEnabled => {
      if (!isFlagEnabled) {
        // go to home page
        router.navigateByUrl('/');
      }
    }),
  );
};
